import { useEffect, useState } from "react";
import Modal from "@/components/modal/Modal";
import { literals } from "@/constants/literals";
import useExitsDataStore from "@/store/useExitsData.store";
import { createGroup, createOrder } from "@/lib/services/order";
import { uploadImagesConcurrent } from "@/lib/services/upload-images";
import { appToast } from "@/lib/toast";
import { useNavigate } from "react-router-dom";
import { BASE_URL, EXITS_INITAL_STATE } from "@/constants/constants";
import { useLoader } from "@/store/useLoader";

import ConfirmPresendsStep from "./ConfirmPresendsStep";
import ConfirmPackagingStep from "./ConfirmPackagingStep";
import type { ExitsOrderData } from "@/types/types.d";
import DiscardOrderModal from "@/pages/capture/components/DiscardOrderModal";

const ConfirmExitsModal = ({
  onClose,
  viewModal,
}: {
  onClose: () => void;
  viewModal: boolean;
}) => {
  const { exitsData, exitsGroupData } = useExitsDataStore();
  const navigate = useNavigate();
  const { upLoader, downLoader } = useLoader();
  const [confirmDiscardModal, setConfirmDiscardModal] = useState(false);
  const [exitsDataToConfirm, setexitsDataToConfirm] =
    useState<ExitsOrderData>(EXITS_INITAL_STATE);

  useEffect(() => {
    if (viewModal) {
      const newexitsDatatoConfirm = { ...exitsData, ...exitsGroupData };
      newexitsDatatoConfirm.ownPackagingOut =
        newexitsDatatoConfirm.ownPackaging || false;
      newexitsDatatoConfirm.iberiaPackagingOut =
        newexitsDatatoConfirm.iberiaPackagingOut || false;
      setexitsDataToConfirm(newexitsDatatoConfirm);
    }
  }, [viewModal, exitsData, exitsGroupData]);

  const openConfirmDiscardModal = () => setConfirmDiscardModal(true);

  const closeModals = () => {
    setConfirmDiscardModal(false);
    onClose();
  };

  const closeConfirmDiscardModal = () => setConfirmDiscardModal(false);

  const saveOrder = async () => {
    if (!exitsGroupData?.length) return;
    const params: any = { ...exitsDataToConfirm };

    try {
      upLoader();
      const order = await createOrder(params);

      const groupsPromises = exitsGroupData.map((group) =>
        createGroup({
          orderId: order.id,
          description: group.description,
          visible: group.visibleForClient,
        }),
      );
      const groupsList = await Promise.all(groupsPromises);

      type PhotoItem = { file: File; imageGroupId: number; route?: string };
      const photosList: PhotoItem[] = [];

      for (let i = 0; i < exitsGroupData.length; i += 1) {
        const group = exitsGroupData[i];
        const createdGroupId = groupsList[i].id;

        group.photos.forEach((photo: any) => {
          const file: File =
            photo?.file instanceof File
              ? photo.file
              : photo instanceof File
                ? photo
                : undefined;
          if (!file) return;
          photosList.push({
            file,
            imageGroupId: createdGroupId,
            route: photo?.route || undefined,
          });
        });
      }

      // Buckets por (imageGroupId, route)
      type Bucket = { imageGroupId: number; route?: string; files: File[] };
      const bucketsMap = new Map<string, Bucket>();
      for (const p of photosList) {
        const key = `${p.imageGroupId}|${p.route || ""}`;
        const bucket = bucketsMap.get(key);
        if (bucket) {
          bucket.files.push(p.file);
        } else {
          bucketsMap.set(key, {
            imageGroupId: p.imageGroupId,
            route: p.route,
            files: [p.file],
          });
        }
      }
      const buckets = Array.from(bucketsMap.values());

      // Concurrencia de buckets y progreso
      const BUCKET_CONCURRENCY = 3;
      const FILES_CONCURRENCY_PER_BUCKET = 6;

      const runBucketsWithConcurrency = async <T,>(
        items: Bucket[],
        worker: (b: Bucket) => Promise<T>,
        concurrency: number,
      ): Promise<T[]> => {
        const results: T[] = [];
        let index = 0;
        const runners = new Array(Math.min(concurrency, items.length))
          .fill(0)
          .map(async () => {
            while (index < items.length) {
              const current = index++;
              results[current] = await worker(items[current]);
            }
          });
        await Promise.all(runners);
        return results;
      };

      // Mapa de progreso por bucket (key: imageGroupId)
      const progressByBucket = new Map<number, number>();

      const bucketResults = await runBucketsWithConcurrency(
        buckets,
        async (bucket) => {
          // Inicializa progreso a 0 por bucket
          progressByBucket.set(bucket.imageGroupId, 0);

          const res = await uploadImagesConcurrent(
            bucket.files,
            bucket.imageGroupId,
            bucket.route,
            {
              concurrency: FILES_CONCURRENCY_PER_BUCKET,
              onProgress: (done) => {
                // progreso por bucket
                progressByBucket.set(bucket.imageGroupId, done);
              },
            },
          );
          return { bucket, ...res };
        },
        BUCKET_CONCURRENCY,
      );

      // Resumen y cierre
      const totalOk = bucketResults.reduce(
        (acc, r) => acc + r.ok_files.length,
        0,
      );
      const totalKo = bucketResults.reduce(
        (acc, r) => acc + r.ko_files.length,
        0,
      );

      if (totalKo > 0) throw Error;

      downLoader();
      appToast.success(
        literals.correctUpload,
        totalOk === 1
          ? literals.savedOnePicture
          : `${literals.savedPicturesPart1} ${totalOk} ${literals.images}`,
      );
      navigate(`${BASE_URL}/capture`);
    } catch (error) {
      if (error) {
        downLoader();
        appToast.error(literals.aProblem, literals.uploadNoPossible);
      }
    }
  };

  const changeOwnPackagingOut = () => {
    setexitsDataToConfirm((previusState) => {
      const newPackagingOutState = {
        ownPackagingOut: !previusState.ownPackagingOut,
        iberiaPackagingOut:
          previusState.ownPackagingOut === false
            ? false
            : previusState.iberiaPackagingOut,
      };
      return {
        ...previusState,
        ...newPackagingOutState,
      };
    });
  };

  const changeIberiaPackaging = () => {
    setexitsDataToConfirm((previusState) => {
      return {
        ...previusState,
        iberiaPackagingOut: !previusState.iberiaPackagingOut,
      };
    });
  };

  const closeMainModal = () => {
    if (exitsData.step === literals.packagingAction) {
      return (
        <ConfirmPackagingStep
          orderData={exitsDataToConfirm}
          changeOwnPackagingOut={changeOwnPackagingOut}
          changeIberiaPackaging={changeIberiaPackaging}
          saveOrder={saveOrder}
          openConfirmDiscardModal={openConfirmDiscardModal}
        />
      );
    }
    return (
      <ConfirmPresendsStep
        saveOrder={saveOrder}
        openConfirmDiscardModal={openConfirmDiscardModal}
      />
    );
  };

  return (
    <>
      {!confirmDiscardModal ? (
        <Modal
          title={literals.confirmation}
          opened={viewModal}
          onClose={onClose}
        >
          {closeMainModal()}
        </Modal>
      ) : (
        <DiscardOrderModal
          closeDiscardModal={closeConfirmDiscardModal}
          closeModalsGroup={closeModals}
        />
      )}
    </>
  );
};

export default ConfirmExitsModal;
